import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PersonOutline as PersonIcon,
  PersonOffOutlined as PersonOffIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonUserIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import axiosInstance from '../../config/axios';
import { useSnackbar } from 'notistack';

const GestionUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/usuarios');
      const usuariosData = Array.isArray(response.data) ? response.data :
                          response.data.usuarios ? response.data.usuarios :
                          response.data.data ? response.data.data : [];
      
      const usuariosProcesados = usuariosData.map(user => ({
        _id: user._id,
        nombre: user.nombre || '',
        apellidos: user.apellidos || '',
        email: user.email || '',
        rol: user.rol || 'usuario',
        estado: user.estado || 'Activo'
      }));
      
      setUsuarios(usuariosProcesados);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      setError('Error al cargar los usuarios. ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async (userId, currentRole) => {
    let newRole;
    // Rotación de roles: paciente -> médico -> admin -> paciente
    switch (currentRole) {
      case 'paciente':
        newRole = 'medico';
        break;
      case 'medico':
        newRole = 'admin';
        break;
      case 'admin':
        newRole = 'paciente';
        break;
      default:
        newRole = 'paciente';
    }

    try {
      const response = await axiosInstance.put(`/admin/usuarios/${userId}/rol`, { rol: newRole });
      
      if (response.data) {
        setUsuarios(prevUsuarios =>
          prevUsuarios.map(usuario =>
            usuario._id === userId ? { ...usuario, rol: newRole } : usuario
          )
        );
        
        let mensaje;
        switch (newRole) {
          case 'admin':
            mensaje = 'Usuario promovido a Administrador exitosamente';
            break;
          case 'medico':
            mensaje = 'Usuario promovido a Médico exitosamente';
            break;
          default:
            mensaje = 'Usuario cambiado a Paciente exitosamente';
        }

        enqueueSnackbar(mensaje, { 
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      }
    } catch (error) {
      console.error('Error:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Error al cambiar el rol del usuario',
        { 
          variant: 'error',
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }
      );
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Activo' ? 'Inactivo' : 'Activo';
      const response = await axiosInstance.put(`/admin/usuarios/${userId}/estado`, { estado: newStatus });
      
      if (response.data.success) {
        setUsuarios(prevUsuarios =>
          prevUsuarios.map(usuario =>
            usuario._id === userId ? { ...usuario, estado: newStatus } : usuario
          )
        );
        enqueueSnackbar(`Usuario ${newStatus.toLowerCase()} exitosamente`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Error al cambiar el estado del usuario:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error al cambiar el estado del usuario', { variant: 'error' });
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axiosInstance.delete(`/admin/usuarios/${userId}`);
      
      setUsuarios(prevUsuarios => prevUsuarios.filter(usuario => usuario._id !== userId));
      
      enqueueSnackbar('Usuario eliminado exitosamente', { 
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      });
      
      setDialogOpen(false);
      setUserToDelete(null);
      
      await fetchUsuarios();
    } catch (error) {
      console.error('Error:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Error al eliminar el usuario',
        { 
          variant: 'error',
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }
      );
    }
  };

  const handleDeleteClick = (usuario) => {
    setUserToDelete(usuario);
    setDialogOpen(true);
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axiosInstance.post(`/admin/usuarios/${userId}/reiniciar-password`);
      
      if (response.data.passwordTemporal) {
        enqueueSnackbar(
          `Contraseña temporal generada: ${response.data.passwordTemporal}`,
          { 
            variant: 'info',
            autoHideDuration: 10000,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          }
        );
      }
      
      setResetPasswordDialog(false);
      setUserToReset(null);
    } catch (error) {
      console.error('Error:', error);
      enqueueSnackbar(
        error.response?.data?.mensaje || 'Error al reiniciar la contraseña',
        { 
          variant: 'error',
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        }
      );
    }
  };

  const handleResetPasswordClick = (usuario) => {
    setUserToReset(usuario);
    setResetPasswordDialog(true);
  };

  const getRoleColor = (rol) => {
    switch (rol) {
      case 'admin':
        return 'error';
      case 'medico':
        return 'primary';
      case 'enfermero':
        return 'success';
      case 'paciente':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusColor = (estado) => {
    return estado === 'Activo' ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Gestión de Usuarios
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Apellidos</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usuarios.map((usuario) => (
              <TableRow key={usuario._id}>
                <TableCell>{usuario.nombre || '(Sin nombre)'}</TableCell>
                <TableCell>{usuario.apellidos || '(Sin apellidos)'}</TableCell>
                <TableCell>{usuario.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={usuario.rol}
                    color={getRoleColor(usuario.rol)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={usuario.estado}
                    color={getStatusColor(usuario.estado)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    justifyContent: 'flex-start',
                    minWidth: '200px'
                  }}>
                    {/* Control de Rol */}
                    <Tooltip title={`Cambiar rol: ${usuario.rol === 'admin' ? 'Administrador → Paciente' : usuario.rol === 'medico' ? 'Médico → Administrador' : 'Paciente → Médico'}`}>
                      <IconButton
                        onClick={() => handleChangeRole(usuario._id, usuario.rol)}
                        sx={{
                          color: usuario.rol === 'admin' ? 'error.main' : usuario.rol === 'medico' ? 'primary.main' : 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: usuario.rol === 'admin' ? 'text.secondary' : usuario.rol === 'medico' ? 'error.main' : 'primary.main',
                          }
                        }}
                      >
                        {usuario.rol === 'admin' ? <AdminIcon /> : usuario.rol === 'medico' ? <PersonIcon /> : <PersonUserIcon />}
                      </IconButton>
                    </Tooltip>

                    {/* Control de Estado */}
                    <Tooltip title={usuario.estado === 'Activo' ? 'Desactivar usuario' : 'Activar usuario'}>
                      <IconButton
                        onClick={() => handleToggleStatus(usuario._id, usuario.estado)}
                        sx={{
                          color: usuario.estado === 'Activo' ? 'success.main' : 'text.disabled',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: usuario.estado === 'Activo' ? 'error.main' : 'success.main',
                          },
                          '& .MuiSvgIcon-root': {
                            transition: 'all 0.3s ease',
                            transform: usuario.estado === 'Activo' ? 'none' : 'scale(1, -1)',
                          }
                        }}
                      >
                        {usuario.estado === 'Activo' ? <PersonIcon /> : <PersonOffIcon />}
                      </IconButton>
                    </Tooltip>

                    {/* Botón Reiniciar Contraseña */}
                    <Tooltip title="Reiniciar contraseña">
                      <IconButton
                        onClick={() => handleResetPasswordClick(usuario)}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'warning.main'
                          }
                        }}
                      >
                        <LockResetIcon />
                      </IconButton>
                    </Tooltip>

                    {/* Botón Eliminar */}
                    <Tooltip title="Eliminar usuario">
                      <IconButton
                        onClick={() => handleDeleteClick(usuario)}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'error.main'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"¿Confirmar eliminación?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar al usuario {userToDelete?.nombre} {userToDelete?.apellidos}?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogOpen(false)}
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleDeleteUser(userToDelete?._id)}
            color="error"
            autoFocus
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de reinicio de contraseña */}
      <Dialog
        open={resetPasswordDialog}
        onClose={() => setResetPasswordDialog(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          {"¿Confirmar reinicio de contraseña?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas reiniciar la contraseña del usuario {userToReset?.nombre} {userToReset?.apellidos}?
            Se generará una contraseña temporal que el usuario deberá cambiar en su próximo inicio de sesión.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetPasswordDialog(false)}
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleResetPassword(userToReset?._id)}
            color="warning"
            autoFocus
          >
            Reiniciar Contraseña
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionUsuarios; 